import CheckedCircleIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/CheckedCircle.js';
import { Box, BoxProps, Button, Divider, useTheme } from '@mui/material';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import type { Theme } from '@mui/material/styles';
import { forwardRef } from 'react';
import {
  atlasFontTextBody,
  atlasRadiusMd,
  atlasSpacing,
  atlasToastAlertContainerSx,
  atlasToastAlertIconStripSx,
  type AtlasLensTheme,
} from '../Form/atlasLensStyles';
import { resolveAtlasLensColors } from '../Form/formFieldSpec';
import { ATLAS_LENS_SEMANTIC_VARS } from '../Form/atlasLensTokens';

export type AtlasSuccessActionToastProps = {
  message: string;
  dismissLabel: string;
  onDismiss: () => void;
  actionLabel?: string;
  onAction?: () => void;
} & Omit<BoxProps, 'children'>;

/** Figma success toast with action buttons — dark surface, green accent strip, checkmark icon, message, action buttons. */
export const AtlasSuccessActionToast = forwardRef<HTMLDivElement, AtlasSuccessActionToastProps>(
  function AtlasSuccessActionToast(
    { message, dismissLabel, onDismiss, actionLabel, onAction, ...rest },
    ref,
  ) {
    const theme = useTheme() as Theme & LensTheme;
    const atlasTheme = theme as AtlasLensTheme;
    const { tokens } = theme;
    const { toastSurface, typeInverse } = resolveAtlasLensColors(theme);
    const statusSuccessDefault =
      tokens?.semantic?.color?.status?.success?.default?.value ??
      ATLAS_LENS_SEMANTIC_VARS.statusSuccessDefault;
    const borderColor =
      tokens?.semantic?.color?.ui?.divider?.default?.value ??
      ATLAS_LENS_SEMANTIC_VARS.dividerDefault;

    return (
      <Box
        ref={ref}
        {...rest}
        role="status"
        aria-live="polite"
        data-qa-anchor="atlas-toast-success-action"
        sx={{
          ...atlasToastAlertContainerSx(atlasTheme),
          background: toastSurface,
          border: `1px solid ${borderColor}`,
        }}
      >
        <Box
          sx={{
            ...atlasToastAlertIconStripSx(atlasTheme),
            background: statusSuccessDefault,
          }}
        >
          <Box sx={{ display: 'flex', color: toastSurface }}>
            <CheckedCircleIcon size="md" />
          </Box>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '2')}`,
            gap: atlasSpacing(atlasTheme, '1'),
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '2')} ${atlasSpacing(atlasTheme, '2')}`,
              alignSelf: 'stretch',
            }}
          >
            <Box
              sx={{
                ...atlasFontTextBody(atlasTheme),
                color: typeInverse,
                flex: 1,
              }}
            >
              {message}
            </Box>
          </Box>
          <Divider
            sx={{
              width: '100%',
              borderColor,
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              padding: `${atlasSpacing(atlasTheme, '1')} ${atlasSpacing(atlasTheme, '1')} 0`,
              gap: '12px',
              alignSelf: 'stretch',
            }}
          >
            <Button
              variant="text"
              size="medium"
              onClick={onDismiss}
              sx={{
                color: typeInverse,
                borderRadius: atlasRadiusMd(atlasTheme),
                textTransform: 'none',
                fontWeight: tokens?.core?.fontWeight?.semiBold?.value ?? 600,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                },
              }}
            >
              {dismissLabel}
            </Button>
            {actionLabel && onAction ? (
              <Button
                variant="text"
                size="medium"
                onClick={onAction}
                sx={{
                  color: typeInverse,
                  borderRadius: atlasRadiusMd(atlasTheme),
                  textTransform: 'none',
                  fontWeight: tokens?.core?.fontWeight?.semiBold?.value ?? 600,
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                {actionLabel}
              </Button>
            ) : null}
          </Box>
        </Box>
      </Box>
    );
  },
);
